import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Pause, Play } from "lucide-react";

const AUTO_ADVANCE_MS = 5500;

export default function HeroSlideshow({ schools }) {
  const slides = (schools || []).filter((s) => s.image_url).slice(0, 6);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);

  const go = useCallback(
    (dir) => {
      if (slides.length === 0) return;
      setIndex((i) => (i + dir + slides.length) % slides.length);
    },
    [slides.length]
  );

  useEffect(() => {
    if (!playing || slides.length <= 1) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [playing, slides.length]);

  if (slides.length === 0) {
    return (
      <div className="aspect-[16/9] md:aspect-[21/9] rounded-3xl bg-stone-100 animate-pulse" />
    );
  }

  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-stone-200 bg-stone-900"
      data-testid="hero-slideshow"
      aria-roledescription="carousel"
    >
      <div className="relative aspect-[16/9] md:aspect-[21/9]">
        {slides.map((s, i) => (
          <div
            key={s.id}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === index ? 1 : 0, pointerEvents: i === index ? "auto" : "none" }}
            aria-hidden={i !== index}
          >
            <img
              src={s.image_url}
              alt={s.name}
              className="w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(10,11,14,0.25) 0%, rgba(10,11,14,0.65) 70%, rgba(10,11,14,0.85) 100%)",
              }}
            />
            <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8 md:p-10">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-[#DAA520] text-stone-900 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full mb-3">
                  Rank · #{s.rank}
                </div>
                <h3
                  className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight"
                  data-testid={`slide-title-${s.rank}`}
                >
                  {s.name}
                </h3>
                <div className="mt-2 flex items-center gap-2 text-stone-200 text-sm">
                  <MapPin className="w-4 h-4 text-[#DAA520]" />
                  <span>{s.district}</span>
                  {s.year_established ? (
                    <>
                      <span className="text-stone-400">·</span>
                      <span>Since {s.year_established}</span>
                    </>
                  ) : null}
                </div>
                <p className="mt-3 text-stone-200 text-sm sm:text-base max-w-2xl line-clamp-2 hidden sm:block">
                  {s.description}
                </p>
                <Link
                  to={`/schools/${s.slug || s.id}`}
                  data-testid={`slide-cta-${s.rank}`}
                  className="mt-4 inline-block bg-[#C85A32] hover:bg-[#A84624] text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
                >
                  View profile →
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <button
        type="button"
        onClick={() => go(-1)}
        data-testid="slideshow-prev"
        aria-label="Previous slide"
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        data-testid="slideshow-next"
        aria-label="Next slide"
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots + play/pause */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          data-testid="slideshow-toggle"
          aria-label={playing ? "Pause slideshow" : "Play slideshow"}
          className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur border border-white/20 text-white flex items-center justify-center transition-colors"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      </div>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setIndex(i)}
            data-testid={`slideshow-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-[#DAA520]" : "w-4 bg-white/40 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
